import { z } from 'zod';

import type { PaymentStatus } from './booking.js';

/**
 * F20 — Off-Platform Payment Marking frozen API contracts.
 *
 * See plans/20_F20_off_platform_payment_marking.md (Wave 1 / Task 1) for the
 * founder-frozen shape of every Zod schema and type alias below. Wave 2
 * sub-agents (2A backend / 2B B2C / 2C B2B / 2D admin) develop against these
 * contracts in parallel — DO NOT rename, drop, or add fields without
 * escalating to the orchestrator first (contract-change protocol).
 *
 * F20 owns the venue-staff "Mark paid" + "Mark refunded" surface, the
 * customer self-claim "I've paid" hint, the customer dispute lifecycle,
 * and the admin disputes-queue + payments-activity-feed. Every mutation
 * inserts one append-only `payment_marks` ledger row in the same transaction
 * as F17's `BookingService.markPaymentStatus` call (Decision 3 / Decision 4).
 *
 * Trust-based marking (Decision 1): venue mark → instantly `paid`; no admin
 * approval queue. `pending_confirmation` is reserved for the customer self-claim
 * hint only (Decision 2). Screenshot upload is intentionally skipped at MVP
 * (Decision 5) — F20 ships text-only marks (method + reference + notes).
 */

// ─── Enum constants ──────────────────────────────────────────────────────────

/**
 * Off-platform payment methods supported at MVP. F21 will let venues opt
 * in/out per-venue via the `PaymentConfigProvider`; F20 ships a hardcoded
 * venue-agnostic stub that returns all four (Decision 11).
 */
export const PAYMENT_METHODS = ['cash', 'whish', 'omt', 'other'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/**
 * Who took the action on this payment mark. The 4-way split feeds F44
 * financial-dashboard rollups + F45 admin-analytics method-mix charts.
 * `system` is reserved for future automated reconciliation (e.g. F19 Whish
 * webhook ingestion); never written by F20 service code.
 */
export const PAYMENT_MARK_ACTOR_KINDS = ['venue_staff', 'customer', 'admin', 'system'] as const;
export type PaymentMarkActorKind = (typeof PAYMENT_MARK_ACTOR_KINDS)[number];

/**
 * Lifecycle of a customer-filed dispute (Decision 7). Single-open-per-booking
 * is enforced via the partial unique index
 * `payment_disputes_one_open_per_booking_idx`; second open file returns
 * 409 `DISPUTE_ALREADY_OPEN`. The two `resolved_*` outcomes drive different
 * downstream effects:
 *   - `resolved_customer_correct` → admin force-transitions `payment_status`
 *     back to `unpaid` via F17 (Decision 7); F23 reverses any accrued
 *     commission via the `payment.dispute_resolved_customer_correct` event.
 *   - `resolved_venue_correct` → `payment_status` stays `paid`; F23 takes no
 *     action.
 */
export const PAYMENT_DISPUTE_STATUSES = [
  'open',
  'resolved_customer_correct',
  'resolved_venue_correct',
  'withdrawn',
] as const;
export type PaymentDisputeStatus = (typeof PAYMENT_DISPUTE_STATUSES)[number];

/**
 * Outcome enum for `ResolveDisputeInputSchema`. Subset of dispute statuses —
 * admin can only resolve to one of the two terminal-correct states (withdrawn
 * is set by the customer via the withdraw route, not by admin).
 */
export const DISPUTE_RESOLUTION_OUTCOMES = ['customer_correct', 'venue_correct'] as const;
export type DisputeResolutionOutcome = (typeof DISPUTE_RESOLUTION_OUTCOMES)[number];

/**
 * F21 — Per-venue billing model (Decision 3). All three values are admitted by
 * the schema CHECK and exposed in the B2B UI from day one. At MVP only
 * `commission_only` is operational; `subscription_only` and `hybrid` render with
 * an "Activates with F38" banner and have no effect on F23 commission accrual.
 * F23 reads this value via `PaymentConfigProvider.getVenuePaymentConfig` and
 * gates accrual; F38 flips the inert values to operational.
 */
export const BILLING_MODELS = ['commission_only', 'subscription_only', 'hybrid'] as const;
export type BillingModel = (typeof BILLING_MODELS)[number];

export const PAYMENT_ERROR_CODES = [
  // mark_paid / mark_refunded / confirm_claim / reject_claim
  'PAYMENT_MARK_INVALID_TRANSITION',
  'PAYMENT_MARK_REQUIRES_METHOD',
  'PAYMENT_MARK_CONFIRMED_BOOKINGS_ONLY',
  'PAYMENT_MARK_WALK_IN_FORBIDDEN',
  // customer self-claim
  'PAYMENT_CLAIM_NOT_BOOKER',
  'PAYMENT_CLAIM_INVALID_FROM_STATE',
  // FIX 4.11 (D-PAY) — the booking is not pending, confirmed or completed.
  'PAYMENT_CLAIM_NOT_ALLOWED',
  // disputes
  'DISPUTE_ALREADY_OPEN',
  'DISPUTE_NOT_BOOKER',
  'DISPUTE_INVALID_FROM_STATE',
  'DISPUTE_NOT_OPEN',
  // PaymentConfigProvider (F20 stub + F21 DB-backed impl)
  'PAYMENT_CONFIG_NOT_AVAILABLE',
  // F21 — per-venue payment-config writes (owner + admin override)
  'PAYMENT_CONFIG_NOT_FOUND',
  'PAYMENT_CONFIG_STALE',
  'PAYMENT_CONFIG_ADMIN_OVERRIDE_ACTIVE',
  'PAYMENT_CONFIG_INVALID_METHOD_FIELDS',
  'PAYMENT_CONFIG_AT_LEAST_ONE_METHOD',
  'PAYMENT_CONFIG_BILLING_MODEL_INERT',
  'PAYMENT_CONFIG_FORBIDDEN',
] as const;
export type PaymentErrorCode = (typeof PAYMENT_ERROR_CODES)[number];

// ─── Zod input schemas ───────────────────────────────────────────────────────

const referenceField = z.string().min(1).max(120).optional();
const notesField = z.string().max(500).optional();

/**
 * `POST /me/venues/:venueId/bookings/:bookingId/payment` body when the route
 * handler resolves `intent === 'mark_paid'` (current `payment_status === 'unpaid'`,
 * target `paid`) OR `intent === 'confirm_claim'` (current `pending_confirmation`,
 * target `paid`). The route handler dispatches via the intent discriminator
 * declared inline; this exported schema is the shared body shape for both
 * intents.
 *
 * `intent === 'reject_claim'` uses an inline route-handler schema
 * (`{ reason: string }`) and is NOT exported here — rejection carries no
 * `method`/`reference`/`notes` because no money changed hands.
 */
export const MarkPaidByVenueInputSchema = z.object({
  method: z.enum(PAYMENT_METHODS),
  reference: referenceField,
  notes: notesField,
});
export type MarkPaidByVenueInput = z.infer<typeof MarkPaidByVenueInputSchema>;

/**
 * `POST /me/venues/:venueId/bookings/:bookingId/refund` body. Reads F17's
 * pre-computed `bookings.refund_amount` (set on cancellation); venue staff
 * acknowledge the refund and pick the method they used. Per Decision 6, F20
 * never re-runs the refund computation and never lets the venue enter a
 * custom amount.
 */
export const MarkRefundedByVenueInputSchema = z.object({
  method: z.enum(PAYMENT_METHODS),
  reference: referenceField,
  notes: notesField,
});
export type MarkRefundedByVenueInput = z.infer<typeof MarkRefundedByVenueInputSchema>;

/**
 * `POST /me/bookings/:bookingId/claim-paid` body. Per Decision 2 the customer
 * does NOT submit a method, reference, or amount — they just signal "I paid".
 * Venue staff supply the financial fields when they Confirm Claim. Empty
 * body, strict mode to reject any unexpected field.
 */
export const CustomerClaimPaidInputSchema = z.object({}).strict();
export type CustomerClaimPaidInput = z.infer<typeof CustomerClaimPaidInputSchema>;

/**
 * `POST /me/bookings/:bookingId/disputes` body. Customer must enter a reason
 * (5–500 chars) so the admin queue has context. Booking must currently be at
 * `payment_status === 'paid'` (Decision 7) — service layer asserts and returns
 * `DISPUTE_INVALID_FROM_STATE` otherwise.
 */
export const FileDisputeInputSchema = z.object({
  reason: z.string().min(5).max(500),
});
export type FileDisputeInput = z.infer<typeof FileDisputeInputSchema>;

/**
 * `POST /admin/payment-disputes/:id/resolve` body. Admin picks one of the
 * two outcomes + optionally records resolution notes (audit-only). Outcome
 * `customer_correct` triggers F17 force-transition `payment_status → unpaid`
 * + emits `payment.dispute_resolved_customer_correct` which F23 subscribes
 * to for commission reversal (Decision 7 + Decision 16).
 */
export const ResolveDisputeInputSchema = z.object({
  outcome: z.enum(DISPUTE_RESOLUTION_OUTCOMES),
  resolutionNotes: z.string().max(1000).optional(),
});
export type ResolveDisputeInput = z.infer<typeof ResolveDisputeInputSchema>;

// ─── Read-side type aliases (server → client projections) ────────────────────

/**
 * One row of the append-only `payment_marks` domain ledger (Decision 3).
 * Returned by `GET /me/venues/:venueId/bookings/:bookingId/payment-marks`
 * (paginated history pane) and embedded as `recentPaymentMarks` on
 * `BookingDetail` (latest 5). The `actorDisplayName` is resolved at read time
 * by service-layer joins against `users` / `admin_users`; null for `system`
 * actors. `method` is null on customer-self-claim rows (Decision 2) and on
 * rejection rows (no money changed hands).
 */
export type PaymentMark = {
  readonly id: string;
  readonly bookingId: string;
  readonly venueId: string;
  readonly actorKind: PaymentMarkActorKind;
  readonly actorUserId: string | null;
  readonly actorAdminId: string | null;
  readonly actorDisplayName: string | null;
  readonly fromStatus: PaymentStatus;
  readonly toStatus: PaymentStatus;
  readonly method: PaymentMethod | null;
  readonly reference: string | null;
  readonly notes: string | null;
  readonly surface: 'portal' | 'mobile' | 'admin' | 'api' | 'system';
  readonly createdAt: string;
};

/**
 * One row of `payment_disputes` (Decision 7). Single-open-per-booking is
 * DB-enforced; the API never returns multiple `open` rows for the same
 * booking. `paymentStatusAtFiling` is a forensic snapshot — the booking's
 * `payment_status` at the moment the dispute was filed; this lets admin see
 * "the customer disputed this while it was paid; it has since moved to
 * refunded for unrelated reasons" without a separate lookup.
 */
export type PaymentDispute = {
  readonly id: string;
  readonly bookingId: string;
  readonly venueId: string;
  readonly filedByUserId: string;
  readonly reason: string;
  readonly paymentStatusAtFiling: PaymentStatus;
  readonly status: PaymentDisputeStatus;
  readonly resolvedAt: string | null;
  readonly resolvedByAdminId: string | null;
  readonly resolutionNotes: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/**
 * F21 — Per-method payout instruction (Decision 5, hybrid validation). Three
 * branches discriminated by `method`:
 *
 *  - Whish — typed `whishPhone` validated E.164 (loose regex at the type layer;
 *    strict libphonenumber-js LB-default validation at the API service layer
 *    per the project convention documented in `venue-profile.ts`).
 *  - OMT — typed `omtBeneficiary` (2–80 chars) + optional `omtBranch` (2–80).
 *  - Cash / Other — free-form `details` only (no typed fields).
 *
 * Every branch carries `method`, `label`, `details` (the F20 base shape) so
 * existing F20 consumers reading `pi.details` continue to compile. The typed
 * branches ADD their respective fields without removing the F20 base.
 */
type PayoutInstructionBase = {
  readonly label: string;
  readonly details: string;
};
export type PayoutInstructionWhish = PayoutInstructionBase & {
  readonly method: 'whish';
  readonly whishPhone: string;
};
export type PayoutInstructionOmt = PayoutInstructionBase & {
  readonly method: 'omt';
  readonly omtBeneficiary: string;
  readonly omtBranch?: string;
};
export type PayoutInstructionCashOrOther = PayoutInstructionBase & {
  readonly method: 'cash' | 'other';
};
export type PayoutInstruction =
  | PayoutInstructionWhish
  | PayoutInstructionOmt
  | PayoutInstructionCashOrOther;

/**
 * Return shape of `PaymentConfigProvider.getVenuePaymentConfig(venueId)`
 * (F20 Decision 11). F20 stubbed this with a hardcoded venue-agnostic default;
 * F21 swaps in `DefaultPaymentConfigProvider` reading the `venue_payment_config`
 * table managed via the F12 venue-editor "Payments" tab. Call sites NEVER change.
 *
 * F21 widens the F20 shape ADDITIVELY:
 *   - `payoutInstructions[]` is now a discriminated union (`PayoutInstruction`)
 *     so Whish + OMT branches carry their typed fields. F20 consumers reading
 *     `{ method, label, details }` continue to compile (union narrowing).
 *   - `billingModel` (F21 Decision 3) — F23 reads this to gate commission
 *     accrual. F38 flips the inert values to operational.
 *   - `adminOverride` (F21 Decision 6) — `null` when no override is active; a
 *     populated metadata object when admin has applied a whole-config override.
 *     The B2B Payments tab renders an `<AdminOverrideBanner>` + disables the
 *     form when this is non-null.
 *
 * `payoutInstructions[]` empty → B2C UI renders the "Please ask venue at desk"
 * fallback. Non-empty → per-method instruction cards.
 */
export type VenuePaymentConfig = {
  readonly acceptedMethods: readonly PaymentMethod[];
  readonly payoutInstructions: readonly PayoutInstruction[];
  readonly billingModel: BillingModel;
  readonly adminOverride: null | {
    readonly setByAdminDisplayName: string;
    readonly setAt: string;
    readonly reason: string;
  };
};

/**
 * Read shape for `GET /me/bookings/:bookingId/payment-instructions`. Augments
 * `VenuePaymentConfig` with the contextual booking + venue identification the
 * B2C `PaymentInstructionsCard` (web) / `PaymentInstructionsScreen` (mobile)
 * need to render the right copy ("Pay $X to {venueName} via …").
 */
export type PaymentInstructionsResponse = VenuePaymentConfig & {
  readonly venueId: string;
  readonly venueName: string;
  readonly bookingId: string;
  readonly currentPaymentStatus: PaymentStatus;
};

// ─── F21 — Per-Venue Payment Configuration Zod input schemas ─────────────────
//
// See plans/21_F21_per_venue_payment_config.md (Wave 1 / Task 1.1) for the
// founder-frozen shape. Wave 2A backend + Wave 2B frontend both consume these
// schemas; client-side validation in the Payments tab uses the same schemas
// the server applies at PATCH/PUT time so messages line up.

const labelField = z.string().min(1).max(40);
const detailsField = z.string().max(200);
const whishPhoneField = z.string().regex(/^\+\d{6,20}$/, 'whishPhone must be E.164');
const omtBeneficiaryField = z.string().min(2).max(80);
const omtBranchField = z.string().min(2).max(80).optional();

const WhishInstructionSchema = z.object({
  method: z.literal('whish'),
  label: labelField,
  details: detailsField,
  whishPhone: whishPhoneField,
});
const OmtInstructionSchema = z.object({
  method: z.literal('omt'),
  label: labelField,
  details: detailsField,
  omtBeneficiary: omtBeneficiaryField,
  omtBranch: omtBranchField,
});
const CashInstructionSchema = z.object({
  method: z.literal('cash'),
  label: labelField,
  details: detailsField,
});
const OtherInstructionSchema = z.object({
  method: z.literal('other'),
  label: labelField,
  details: detailsField,
});

/**
 * F21 — payout instruction discriminated union schema. Split into 4 branches
 * (not 3) because Zod `discriminatedUnion` requires a single-literal
 * discriminator per branch (F20 lesson learned — captured in
 * docs/CLAUDE.md F20 section).
 */
export const PayoutInstructionSchema = z.discriminatedUnion('method', [
  WhishInstructionSchema,
  OmtInstructionSchema,
  CashInstructionSchema,
  OtherInstructionSchema,
]);

/**
 * `PATCH /me/venues/:venueId/payment-config` body — venue owner edit. Service
 * additionally asserts:
 *   - every element of `payoutInstructions[].method` ∈ `acceptedMethods`
 *     (no orphaned instructions; if Whish is not accepted, no Whish instruction)
 *   - `acceptedMethods` contains at least one element (also a DB CHECK)
 */
export const UpdateVenuePaymentConfigInputSchema = z.object({
  acceptedMethods: z.array(z.enum(PAYMENT_METHODS)).min(1),
  payoutInstructions: z.array(PayoutInstructionSchema),
  billingModel: z.enum(BILLING_MODELS),
});
export type UpdateVenuePaymentConfigInput = z.infer<typeof UpdateVenuePaymentConfigInputSchema>;

/**
 * `PUT /admin/venues/:venueId/payment-config/override` body — admin whole-config
 * replace (Decision 6). Same shape as the owner update PLUS a mandatory
 * `reason` (≥10 chars) audit-logged via F06's `onSend` hook AND included in the
 * F09 notification payload sent to the venue owner.
 */
export const ApplyAdminOverrideInputSchema = UpdateVenuePaymentConfigInputSchema.extend({
  reason: z.string().min(10).max(500),
});
export type ApplyAdminOverrideInput = z.infer<typeof ApplyAdminOverrideInputSchema>;

/**
 * `DELETE /admin/venues/:venueId/payment-config/override` body — admin clears
 * the override (Decision 6). The override snapshot is RETAINED as the venue's
 * current config (founder convention — admin's snapshot becomes the new
 * baseline until the owner edits). Owner regains write access.
 */
export const ClearAdminOverrideInputSchema = z.object({
  reason: z.string().min(10).max(500),
});
export type ClearAdminOverrideInput = z.infer<typeof ClearAdminOverrideInputSchema>;
